import { observer } from "mobx-react-lite";
import React, { useContext, useState } from "react";

import {
  ConfirmDialog,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import {
  EncounterChangeConditionFormValues,
  EncounterStatus
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { SubmissionFormDialogHeader } from "@ui-components/form/submission-form-dialog/SubmissionFormDialogHeader.tsx";
import { FormSubmitButtons } from "@ui-components/form/submission-form/FormSubmitButtons.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";

import { ChangeConditionExpanderButton } from "./ChangeConditionExpanderButton.tsx";
import { ChangeConditionContext } from "./context/ChangeConditionContext.ts";

interface ChangeConditionModalFieldsProps {
  currentEpisodeOfCare?: EpisodeOfCare;
  clinicalRecord: ClinicalRecord;
  encounter: Encounter;
}

export const ChangeConditionModalFields: React.FC<ChangeConditionModalFieldsProps> =
  observer(({ currentEpisodeOfCare, clinicalRecord }) => {
    const { spacing } = useTheme();
    const { clinical, core, acc, booking } = useStores();

    const [originalEpisodeOfCare] = useState<EpisodeOfCare | undefined>(
      currentEpisodeOfCare
    );

    const { setShowChangeConditionDialog, showChangeConditionSecondColumn } =
      useContext(ChangeConditionContext);

    const patientName = clinicalRecord?.patient?.fullName ?? "";

    const [showChangeDiagnosisWarning, setShowChangeDiagnosisWarning] =
      useState(false);

    const [
      encounterChangeConditionFormValue,
      setEncounterChangeConditionFormValue
    ] = useState<EncounterChangeConditionFormValues>();

    const nameof = nameOfFactory<EncounterChangeConditionFormValues>();

    const onHandleSubmit = async () => {
      const encounterId = clinicalRecord.chosenEncounterId;

      const chosenEncounter = await clinical.getEncounter(encounterId);

      if (core.hasPermissions(Permission.ClaimWrite)) {
        const claimEncounters = await acc.getClaimEncounters(
          currentEpisodeOfCare?.claim?.id ? currentEpisodeOfCare?.claim?.id : ""
        );

        const claimEncounter = claimEncounters?.find(x => x.id === encounterId);

        if (!claimEncounter && currentEpisodeOfCare?.claim?.id && encounterId)
          await acc.addClaimEncounter({
            claimId: currentEpisodeOfCare?.claim?.id,
            encounterId,
            providerId: chosenEncounter.userId,
            businessRole: chosenEncounter.businessRole
          });
      }

      //add change condition comment
      if (encounterId && encounterChangeConditionFormValue) {
        clinicalRecord.addcChangeConditionComment(
          encounterId,
          encounterChangeConditionFormValue
        );
      }

      // if appointment exist, update the appointment
      if (chosenEncounter.calendarEvent) {
        await booking.updateCalendarEvent({
          id: encounterId,
          episodeOfCareId: currentEpisodeOfCare?.id
        });
      }

      //update encounter for the change condition
      if (
        chosenEncounter.status === EncounterStatus.Closed &&
        currentEpisodeOfCare &&
        chosenEncounter.episodeOfCareId !== currentEpisodeOfCare?.id
      ) {
        clinicalRecord.updateEncounterForChangeCondition(
          encounterId,
          chosenEncounter,
          currentEpisodeOfCare?.id
        );
      }

      //get the updated episode of care clinical data
      if (currentEpisodeOfCare) {
        clinicalRecord.updateReasonForVisitForChangeCondition(
          encounterId,
          currentEpisodeOfCare
        );
      }

      //add append notes
      if (originalEpisodeOfCare && currentEpisodeOfCare) {
        clinicalRecord.addAppendNotesForChangeCondition(
          encounterId,
          originalEpisodeOfCare,
          currentEpisodeOfCare
        );
      }

      await clinicalRecord.loadPatientEpisodesOfCare();
      await clinicalRecord.loadLinkedEncounters();

      setShowChangeConditionDialog(false);
    };

    return (
      <>
        <SubmissionFormDialogHeader
          onCancel={() => {
            setShowChangeConditionDialog(false);
          }}
          title={`Change linked condition for ${patientName}`}
        />
        {showChangeConditionSecondColumn &&
          originalEpisodeOfCare?.diagnoses !==
            currentEpisodeOfCare?.diagnoses && (
            <MessageBar messageBarType={MessageBarType.warning}>
              The selected condition has a different diagnosis. Changing the
              linked condition will update the associated diagnosis.
            </MessageBar>
          )}
        <ChangeConditionExpanderButton
          currentEpisodeOfCare={currentEpisodeOfCare}
        />
        <Stack horizontal styles={{ root: { padding: 16 } }}>
          <TextInputField
            multiline={true}
            name={nameof("reasonForChangeConditionComment")}
            label="Comments"
            resizable={false}
            rows={4}
          />
        </Stack>
        <FormSubmitButtons
          promptOnCancel
          hideButtonsSeparator
          onCancel={() => {
            setShowChangeConditionDialog(false);
          }}
          onSubmit={formApi => {
            setShowChangeDiagnosisWarning(true);
            const values = formApi.getState()
              .values as EncounterChangeConditionFormValues;
            setEncounterChangeConditionFormValue(values);
          }}
          submitButtonProps={{
            id: "submit-statement-form",
            text: "Continue",
            iconProps: undefined
          }}
          styles={{ root: { padding: spacing.l1 } }}
          cancelButtonProps={{ text: "Cancel" }}
        />
        {showChangeDiagnosisWarning && (
          <ConfirmDialog
            hidden={false}
            confirmButtonProps={{ text: "Confirm" }}
            cancelButtonProps={{ text: "Cancel" }}
            onConfirm={() => {
              onHandleSubmit();
              setShowChangeDiagnosisWarning(false);
            }}
            minWidth={500}
            onCancel={() => {
              setShowChangeDiagnosisWarning(false);
            }}
          >
            <Stack tokens={{ childrenGap: 30 }}>
              <Text>
                Consult will NOT be moved until the current Consult or Record
                Update is Saved or Finalised.
              </Text>
            </Stack>
          </ConfirmDialog>
        )}
      </>
    );
  });
